import { FormEvent, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bars } from 'react-loader-spinner';

import { DashboardCustomerContext } from '../DashboardCustomer/Context';
import { canTrackCompany } from './eligibility';
import { estimatedCompletionDate, nextStageBusinessDays } from './estimates';
import { incorporationFileAccept, incorporationFileError } from './fileRules';
import { IncorporationTimeline } from './IncorporationTimeline';
import { useIncorporationTracking } from './useIncorporationTracking';

export const IncorporationTrackingDetails = () => {
  const { t, i18n } = useTranslation();
  const { companyInfo } = useContext(DashboardCustomerContext);
  const { tracking, loading, error, refresh, uploadDocument } = useIncorporationTracking(companyInfo);
  const [file, setFile] = useState<File | null>(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackIsError, setFeedbackIsError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const locale = i18n.language?.startsWith('pt') ? 'pt-BR' : i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
  const formatDateTime = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleFile = (selected: File | null) => {
    if (!selected) {
      setFile(null);
      return;
    }

    const fileError = incorporationFileError(selected);
    if (fileError) {
      setFile(null);
      setFeedback(String(t(`incorporation.errors.${fileError}`) || ''));
      setFeedbackIsError(true);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setFile(selected);
    setFeedback('');
    setFeedbackIsError(false);
  };

  const handleUpload = async (event: FormEvent) => {
    event.preventDefault();
    const pendency = tracking?.pendencies[0];
    if (!pendency || !file || uploading) return;

    setUploading(true);
    const result = await uploadDocument(pendency.id, file);
    setUploading(false);

    if (!result.ok) {
      setFeedback(result.message);
      setFeedbackIsError(true);
      return;
    }

    setFeedback(String(t('incorporation.uploadSuccess', { name: file.name }) || ''));
    setFeedbackIsError(false);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const waitingForTracking = canTrackCompany(companyInfo) && !tracking && !error;

  if (!companyInfo?.id || loading || waitingForTracking) {
    return (
      <div className="flex min-h-[240px] items-center justify-center">
        <Bars height="70" width="70" color="#0C1FAA" visible />
      </div>
    );
  }

  if (!tracking) {
    return (
      <div className="mt-6 flex flex-col gap-6">
        <h2 className="text-xl font-bold text-[#0C1FAA]">{t('incorporation.cardTitle')}</h2>
        <section className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <p className={`text-sm ${error ? 'text-red-600' : 'text-gray-600'}`}>{error || t('incorporation.notStarted')}</p>
          {error && (
            <button type="button" onClick={() => void refresh()} className="mt-4 text-sm font-semibold text-[#0C1FAA] underline">
              {t('incorporation.retry')}
            </button>
          )}
        </section>
      </div>
    );
  }

  const current = tracking.stages.find((stage) => stage.status === 'in_progress' || stage.status === 'waiting_customer');
  const finished = Boolean(tracking.finishedAt) || tracking.stages.every((stage) => stage.status === 'completed');
  const openPendency = tracking.pendencies[0];
  const upcomingDays = tracking.jurisdiction ? nextStageBusinessDays(tracking.stages, tracking.jurisdiction) : null;
  const estimate = tracking.jurisdiction
    ? estimatedCompletionDate(tracking.stages, tracking.jurisdiction).toLocaleDateString(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    : '';

  return (
    <div className="mt-6 flex flex-col gap-6">
      <h2 className="text-xl font-bold text-[#0C1FAA]">{t('incorporation.cardTitle')}</h2>
      {error && (
        <div>
          <p className="text-sm text-red-600">{error}</p>
          <button type="button" onClick={() => void refresh()} className="mt-2 text-sm font-semibold text-[#0C1FAA] underline">
            {t('incorporation.retry')}
          </button>
        </div>
      )}
      {feedback && <p className={`text-sm ${feedbackIsError ? 'text-red-600' : 'text-emerald-700'}`}>{feedback}</p>}

      <section className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <IncorporationTimeline tracking={tracking} />
      </section>

      {(current || openPendency) && (
        <div className={`grid items-stretch gap-6 ${!finished ? 'lg:grid-cols-[minmax(0,1fr)_320px]' : ''}`}>
          <div className="flex h-full flex-col gap-6">
            {current && (
              <section className="flex h-full flex-col rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#0C1FAA]">{t('incorporation.currentStage')}</p>
                <h2 className="mt-1 text-xl font-bold text-gray-900">{t(`incorporation.stages.${current.key}.title`)}</h2>
                <p className="mt-2 text-sm leading-6 text-gray-600">{t(`incorporation.stages.${current.key}.description`)}</p>
                {upcomingDays !== null && (
                  <p className="mt-4 text-sm text-gray-700">{t('incorporation.nextStageEstimate', { count: upcomingDays })}</p>
                )}
                {openPendency && !finished && estimate && (
                  <div className="mt-auto border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('incorporation.estimatedCompletion')}</p>
                    <p className="mt-1 text-base font-bold text-[#0C1FAA]">{estimate}</p>
                    <p className="mt-1 text-sm text-gray-500">
                      {t('incorporation.jurisdictionEstimate', { jurisdiction: tracking.jurisdiction })}
                    </p>
                  </div>
                )}
              </section>
            )}
          </div>

          {openPendency ? (
            <form onSubmit={handleUpload} className="flex h-full flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">{t('incorporation.waitingTitle')}</p>
              <p className="mt-2 text-sm leading-6 text-gray-600">{openPendency.description}</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="mt-4 flex w-full cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-2.5 text-sm font-semibold text-[#0C1FAA] transition-colors hover:border-[#0C1FAA] hover:bg-[#F4F6FF] disabled:opacity-50"
              >
                {t('incorporation.chooseFile')}
              </button>
              <input
                ref={fileInputRef}
                id="pendency-file"
                type="file"
                accept={incorporationFileAccept}
                className="hidden"
                onChange={(event) => handleFile(event.target.files?.[0] ?? null)}
              />
              <p className="mt-2 text-xs text-gray-500">{t('incorporation.fileHint')}</p>
              {file && <p className="mt-2 truncate text-xs text-gray-500">{file.name}</p>}
              <button
                type="submit"
                disabled={!file || uploading}
                className="mt-auto w-full rounded-lg bg-[#0C1FAA] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {uploading ? t('incorporation.sending') : t('incorporation.upload')}
              </button>
            </form>
          ) : (
            !finished &&
            estimate && (
              <section className="flex h-full flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('incorporation.estimatedCompletion')}</p>
                <p className="mt-2 text-lg font-bold text-[#0C1FAA]">{estimate}</p>
                <p className="mt-1 text-sm text-gray-500">
                  {t('incorporation.jurisdictionEstimate', { jurisdiction: tracking.jurisdiction })}
                </p>
              </section>
            )
          )}
        </div>
      )}

      <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">{t('incorporation.history')}</h2>
        {tracking.history.length === 0 ? (
          <p className="mt-4 text-sm text-gray-500">{t('incorporation.historyEmpty')}</p>
        ) : (
          <ul className="mt-4 space-y-4">
            {tracking.history.map((entry) => (
              <li key={entry.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <p className="text-sm font-medium text-gray-900">
                  {t(`incorporation.historyTypes.${entry.type}`, {
                    stage: t(`incorporation.stages.${entry.stageKey}.title`)
                  })}
                </p>
                {entry.note && <p className="mt-1 text-sm text-gray-600">{entry.note}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  {formatDateTime(entry.at)} · {entry.by}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
